import { motion } from "framer-motion";
import { Check, CalendarClock, Truck, Wrench, Headphones, Flag } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";

const COMPACT_IMG =
  "https://images.unsplash.com/photo-1760553121003-93afc4d88ae0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6ODh8MHwxfHNlYXJjaHszfHxyYWNpbmclMjBzaW11bGF0b3IlMjB0cmFkZSUyMHNob3clMjBldmVudHxlbnwwfHx8fDE3OTA5NjE5MDl8MA&ixlib=rb-4.1.0&q=75&w=900";
const PRO_IMG =
  "https://images.unsplash.com/photo-1743649978995-c76212449e15?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTY1MDZ8MHwxfHNlYXJjaHwxfHxyYWNpbmclMjBzaW11bGF0b3IlMjByaWclMjBjb2NrcGl0JTIwbmVvbiUyMGJsdWV8ZW58MHx8fHwxNzkwOTYxOTA5fDA&ixlib=rb-4.1.0&q=75&w=900";
const PREMIUM_IMG =
  "https://images.unsplash.com/photo-1596040078821-9b5f50e71ea5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NzN8MHwxfHNlYXJjaHszfHxjb3Jwb3JhdGUlMjBldmVudCUyMHN0YWdlJTIwc2NyZWVuJTIwZGFya3xlbnwwfHx8fDE3OTA5NjIyODB8MA&ixlib=rb-4.1.0&q=75&w=900";
const MOTION_IMG =
  "https://images.unsplash.com/photo-1771440571270-e27b63085a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxlc3BvcnRzJTIwYXJlbmElMjByYWNpbmclMjBzaW11bGF0b3IlMjBtb3Rpb24lMjBwbGF0Zm9ybXxlbnwwfHx8fDE3OTA5NjE5MDl8MA&ixlib=rb-4.1.0&q=75&w=900";

const PACKAGES = [
  {
    id: "compact",
    name: "COMPACT",
    line: "Racing Activation",
    tag: "Single Event Day",
    img: COMPACT_IMG,
    alt: "Guest driving a compact single-screen racing simulator at an event",
    features: [
      'Aluminum-profile rig + 34" curved ultrawide',
      "Direct-drive wheel & load-cell pedals",
      "Pro operator for up to 8 event hours",
      "Fastest-lap competition & leaderboard",
    ],
    blurb: "Best for dealerships, open houses, and smaller corporate events — roughly 60–100 guest sessions per day.",
    spec: "1 Event Day · Up to 8 Hours",
    badge: null,
    highlighted: false,
  },
  {
    id: "professional",
    name: "PROFESSIONAL",
    line: "Racing Activation",
    tag: "Single Event Day",
    img: PRO_IMG,
    alt: "Triple-screen racing simulator cockpit in neon blue light",
    features: [
      'Triple 32" display configuration',
      "Branded digital leaderboard",
      "Registration & basic lead capture",
      "Your logo in the on-screen experience",
    ],
    blurb: "A premium branded experience for corporate activations, recruiting events, and one-day trade shows.",
    spec: "1 Event Day · Up to 8 Hours",
    badge: null,
    highlighted: false,
  },
  {
    id: "premium",
    name: "PREMIUM",
    line: "Trade Show Activation",
    tag: "Three-Day Event",
    img: PREMIUM_IMG,
    alt: "Branded trade show activation with a large presentation screen",
    features: [
      "Custom in-game car livery in your branding",
      "Live leaderboard + spectator display",
      "Full lead capture with post-event export",
      "Backup hardware & daily calibration",
    ],
    blurb: "A fully managed, branded booth attraction built to generate traffic, capture leads, and bring attendees back.",
    spec: "3 Event Days · Up to 8 Hrs/Day",
    badge: "LEAD-GEN CENTERPIECE",
    highlighted: true,
  },
  {
    id: "motion",
    name: "3DOF MOTION",
    line: "Trade Show Activation",
    tag: "Three-Day Event",
    img: MOTION_IMG,
    alt: "Commercial-grade motion racing simulator platform",
    features: [
      "Everything in Premium Trade Show",
      "Commercial-grade 3DOF motion platform",
      "Pitch, roll & heave synced to the car",
      "Enhanced safety perimeter & screening",
    ],
    blurb: "Our most immersive activation — a centerpiece attendees can see, feel, and remember.",
    spec: "3 Event Days · Up to 8 Hrs/Day",
    badge: "FLAGSHIP · 3DOF MOTION",
    highlighted: false,
  },
];

const FEATURES = [
  { icon: Truck, title: "Delivery", desc: "Metro Detroit based. Travel quoted." },
  { icon: Wrench, title: "Setup", desc: "Professional installation & calibration." },
  { icon: Headphones, title: "On-Site Operation", desc: "Run by professional operators." },
  { icon: Flag, title: "Brand Ready", desc: "Liveries, leaderboards & lead capture." },
];

const PackageCard = ({ pkg, onQuote, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay: index * 0.1, ease: EASE }}
    className={`relative flex flex-col bg-panel border clip-corner group ${
      pkg.highlighted
        ? "border-electric shadow-[0_0_60px_rgba(0,163,255,0.18)] xl:-translate-y-4"
        : "border-line hover:border-slate-500/60"
    } transition-colors duration-300`}
    data-testid={`tier-card-${pkg.id}`}
  >
    {pkg.badge && (
      <div
        className={`absolute top-4 left-4 z-10 font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 ${
          pkg.highlighted ? "bg-electric text-ink font-semibold" : "bg-ink/85 text-electric border border-electric/40 backdrop-blur-sm"
        }`}
        data-testid={`tier-badge-${pkg.id}`}
      >
        {pkg.badge}
      </div>
    )}
    <div className="relative h-40 overflow-hidden">
      <img
        src={pkg.img}
        alt={pkg.alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-panel via-panel/20 to-transparent" />
    </div>
    <div className="flex flex-col flex-1 p-6 -mt-6 relative">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-electric">{pkg.tag}</p>
      <h3 className="font-display text-xl font-black uppercase tracking-tight text-slate-50 mt-2 leading-tight">
        {pkg.name}
        <span className="block text-sm font-bold text-slate-400 tracking-wide mt-0.5">{pkg.line}</span>
      </h3>
      <ul className="mt-4 space-y-2.5">
        {pkg.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[13px] text-slate-300">
            <Check className="h-4 w-4 text-electric shrink-0 mt-0.5" strokeWidth={2.5} />
            {f}
          </li>
        ))}
      </ul>
      <p className="text-[13px] text-slate-400 mt-4 leading-relaxed flex-1">{pkg.blurb}</p>
      <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-line">
        <CalendarClock className="h-4 w-4 text-electric" strokeWidth={1.8} />
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">
          {pkg.spec}
        </span>
      </div>
      <button
        onClick={() => onQuote(pkg.id)}
        data-testid={`tier-quote-btn-${pkg.id}`}
        className={`mt-5 w-full font-display font-bold text-sm uppercase tracking-[0.15em] px-5 py-3.5 clip-corner transition-colors duration-200 ${
          pkg.highlighted
            ? "bg-electric text-ink hover:bg-electric-hover"
            : "border border-slate-500/60 text-slate-100 hover:border-electric hover:text-electric"
        }`}
      >
        Request a Quote
      </button>
    </div>
  </motion.article>
);

export const Packages = ({ onQuote }) => (
  <section id="simulators" className="relative py-20 lg:py-28 bg-ink" data-testid="simulators-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        center
        kicker="Activation Packages"
        title={
          <>
            Four Packages. <span className="text-electric">Endless Possibilities.</span>
          </>
        }
        sub="Professionally operated simulators. Turnkey service. Unforgettable brand experiences."
      />

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mt-14 lg:mt-20 items-start">
        {PACKAGES.map((p, i) => (
          <PackageCard key={p.id} pkg={p} onQuote={onQuote} index={i} />
        ))}
      </div>

      <Reveal delay={0.05}>
        <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto leading-relaxed" data-testid="pricing-footnote">
          Every activation is tailored and quoted per event based on package, dates and location —
          request a quote for a proposal built around yours.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          className="grid grid-cols-2 lg:grid-cols-4 border border-line mt-10 lg:mt-14 bg-panel/40"
          data-testid="feature-bar"
        >
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className={`flex items-start gap-3.5 p-6 lg:p-7 ${i !== 0 ? "lg:border-l border-line" : ""} ${i >= 2 ? "border-t lg:border-t-0 border-line" : ""} ${i % 2 === 1 ? "border-l lg:border-l border-line" : ""}`}
              data-testid={`feature-${i}`}
            >
              <f.icon className="h-5 w-5 text-electric shrink-0 mt-0.5" strokeWidth={1.8} />
              <div>
                <p className="font-display font-bold text-sm text-slate-100 uppercase tracking-wide">{f.title}</p>
                <p className="text-xs text-slate-400 mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
